/**
 * The organization's cameras: the rows `GET /devices` lists, each camera's live status, the
 * writes this session made that the list does not carry yet, and the last name every camera
 * was listed under.
 *
 * The rows are one fetch shared by every consumer, reloaded by `devices` change frames; a burst
 * of them — a sync replays whole batches — costs one request. The statuses arrive only as
 * `cam.<id>` frames and sit apart from the rows, so a reload can never overwrite one that
 * arrived while it was in flight. A camera reads no status before its first frame and once the
 * stream has been down past its grace period. A row holds no status of its own: only the stream
 * says whether a camera is online, so a row cannot contradict it.
 *
 * A camera is added, and its talk settings saved, on its media_server, which uploads the change
 * on a timer of its own; until then the cloud's list answers the old row. A pending write shows
 * what the media_server answered until a load proves the cloud caught up. A rename is a write to
 * the cloud, but the list shows it only after its change frame and a reload; a pending write
 * shows the new name in the meantime.
 */

import { dequal } from 'dequal';
import { atom } from 'jotai';
import { atomWithLazy, selectAtom } from 'jotai/utils';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms } from '../snapshotAtoms';
import { api } from '@/api';
import { readStoredCameraNames, writeStoredCameraNames } from '@/lib/cameraNames';
import { isOnlineStatus } from '@/lib/resourceStatus';
import { MINUTE_MS } from '@/lib/timeUnits';
import type {
    CameraIndex, CameraNames, DevicesSnapshot, FlatDevice, TalkEdit,
} from '@/types/devices';
import { type ResourceStatus, type StreamInput, StreamInputKind } from '@/types/notifications';
import { PortalEvent } from '@/types/portal';

/** One request answers every `devices` change frame that arrives while it waits. */
export const DEVICES_RELOAD_AFTER_MS = 500;

/** An upload that never lands must not hold a camera's row for good. */
export const PENDING_WRITE_LIMIT_MS = 2 * MINUTE_MS;

interface PendingWrite<T> {
    value: T;
    recordedAtMs: number;
    /** When a `devices` change frame named this camera: its row has reached the cloud. */
    namedAtMs: number | null;
}

type PendingWrites<T> = ReadonlyMap<string, PendingWrite<T>>;

const PENDING: DevicesSnapshot = { devices: [], isLoading: true, isLoaded: false, error: null };

const pendingAddsAtom = atom<PendingWrites<FlatDevice>>(new Map());
const pendingTalkEditsAtom = atom<PendingWrites<TalkEdit>>(new Map());
const pendingRenamesAtom = atom<PendingWrites<string>>(new Map());

/** Read from storage at the first read in each store: a label has a name before the list does. */
export const cameraNamesAtom = atomWithLazy((): CameraNames => readStoredCameraNames());

/**
 * Whether a camera's talk and audio fields equal an edit. An edit holds only the fields that
 * the operator changed, so a field it does not hold matches any value. `talkTargets` is a set,
 * so the order the cloud lists it in does not matter.
 */
export function isSameTalk(device: FlatDevice, edit: TalkEdit): boolean {
    const isSameTargets = (editedTexts: readonly string[]): boolean => {
        const listedTexts = device.talkTargets ?? [];
        const editedIds = new Set(editedTexts);
        return listedTexts.length === editedIds.size &&
            listedTexts.every((id) => editedIds.has(id));
    };
    return (edit.talkEnabled === undefined || device.talkEnabled === edit.talkEnabled) &&
        (edit.audioEnabled === undefined || device.audioEnabled === edit.audioEnabled) &&
        (edit.talkTargets === undefined || isSameTargets(edit.talkTargets));
}

function getCameraName(
    camera: FlatDevice | undefined, names: CameraNames, id: string,
): string | undefined {
    return camera?.name || names[id];
}

/** The writes a load leaves pending: neither matched, nor named before it, nor too old. */
function filterStillPending<T>(
    writes: PendingWrites<T>,
    startedAtMs: number,
    matches: (id: string, value: T) => boolean,
): PendingWrites<T> {
    const remainingWrites = new Map<string, PendingWrite<T>>();
    for (const [id, write] of writes) {
        const isNamed = write.namedAtMs !== null && startedAtMs > write.namedAtMs;
        const isExpired = startedAtMs - write.recordedAtMs > PENDING_WRITE_LIMIT_MS;
        if (!isNamed && !isExpired && !matches(id, write.value)) {
            remainingWrites.set(id, write);
        }
    }
    return remainingWrites.size === writes.size ? writes : remainingWrites;
}

/** Ends every pending write the load that started at `startedAtMs` answers. */
const endPendingWritesAtom = atom(
    null,
    (get, set, devices: readonly FlatDevice[], startedAtMs: number): void => {
        // Almost every load ends nothing, and the index below costs one entry for each camera.
        if (get(pendingAddsAtom).size === 0 && get(pendingTalkEditsAtom).size === 0 &&
            get(pendingRenamesAtom).size === 0) {
            return;
        }
        const byId = new Map(devices.map((device) => [device.id, device]));
        set(pendingAddsAtom, filterStillPending(
            get(pendingAddsAtom), startedAtMs, (id) => byId.has(id)));
        set(pendingTalkEditsAtom, filterStillPending(
            get(pendingTalkEditsAtom), startedAtMs, (id, edit) => {
                const device = byId.get(id);
                return device !== undefined && isSameTalk(device, edit);
            }));
        set(pendingRenamesAtom, filterStillPending(
            get(pendingRenamesAtom), startedAtMs, (id, name) => byId.get(id)?.name === name));
    },
);

const cacheCameraNamesAtom = atom(null, (get, set, devices: readonly FlatDevice[]): void => {
    const knownRecord = get(cameraNamesAtom);
    const changedDevices = devices.filter((device) => device.name &&
        knownRecord[device.id] !== device.name);
    if (changedDevices.length === 0) {
        return;
    }
    const names = { ...knownRecord };
    for (const device of changedDevices) {
        names[device.id] = device.name;
    }
    set(cameraNamesAtom, names);
    writeStoredCameraNames(names);
});

const snapshot = snapshotAtoms<DevicesSnapshot>(
    PENDING,
    async () => ({ devices: await api.listDevices(), isLoading: false, isLoaded: true,
        error: null }),
    {
        reloadAfterMs: DEVICES_RELOAD_AFTER_MS,
        onLoaded: ({ devices }, startedAtMs, _get, set): void => {
            set(endPendingWritesAtom, devices, startedAtMs);
            set(cacheCameraNamesAtom, devices);
        },
    },
);

export const devicesAtom = snapshot.value;

// The rows alone, so a reload that only flips `isLoading` rebuilds no list and wakes no reader.
const rowsAtom = atom((get): FlatDevice[] => get(devicesAtom).devices);

/** The listed cameras, with this session's pending writes laid over them. */
export const camerasAtom = atom((get): FlatDevice[] => {
    const rows = get(rowsAtom);
    const adds = get(pendingAddsAtom);
    const edits = get(pendingTalkEditsAtom);
    const renames = get(pendingRenamesAtom);
    if (adds.size === 0 && edits.size === 0 && renames.size === 0) {
        return rows;
    }
    const listedIds = new Set(rows.map((row) => row.id));
    const addedDevices = [...adds].filter(([id]) => !listedIds.has(id)).map(([, add]) =>
        add.value);
    return [...rows, ...addedDevices].map((camera) => {
        const edit = edits.get(camera.id);
        const rename = renames.get(camera.id);
        const editedDevice = edit ? { ...camera, ...edit.value } : camera;
        return rename ? { ...editedDevice, name: rename.value } : editedDevice;
    });
});

/** The listed cameras by id, for a lookup that a loop calls. */
const cameraByIdAtom = atom((get): ReadonlyMap<string, FlatDevice> =>
    new Map(get(camerasAtom).map((camera) => [camera.id, camera])));

/** One camera's row. A reload that leaves the row as it was hands back the same object. */
export const cameraAtom = atomFamily((id: string) =>
    selectAtom(cameraByIdAtom, (byId): FlatDevice | undefined => byId.get(id), dequal));

/** The row's name, else the last one the camera was listed under. */
export const cameraNameAtom = atomFamily((id: string) =>
    atom((get): string | undefined =>
        getCameraName(get(cameraAtom(id)), get(cameraNamesAtom), id)));

/** The cameras by id and their names. A status frame leaves it as it is. */
export const cameraIndexAtom = atom((get): CameraIndex => {
    const byId = get(cameraByIdAtom);
    const names = get(cameraNamesAtom);
    return {
        byId,
        getName: (id: string): string | undefined => getCameraName(byId.get(id), names, id),
    };
});

interface StatusFrame {
    status: ResourceStatus;
    /** The stream's epoch at arrival: a frame from before a `Down` no longer says anything. */
    epoch: number;
}

// One atom for each camera, written only by devicesStreamAtom: a frame evaluates the readers of
// its own camera and of nothing else, however many cameras are listed and read.
const statusFrameAtom = atomFamily((_id: string) => atom<StatusFrame | null>(null));

// Counts the times the stream was down past its grace period. A status from an earlier epoch
// reads as unknown, so `Down` forgets every status in one write.
const streamEpochAtom = atom(0);

/** One camera's status; undefined before its first frame and after the stream was down. */
export const cameraStatusAtom = atomFamily((id: string) =>
    atom((get): ResourceStatus | undefined => {
        const frame = get(statusFrameAtom(id));
        return frame !== null && frame.epoch === get(streamEpochAtom) ? frame.status : undefined;
    }));

/** The statuses of one server's listed cameras, for a choice among them. */
export const serverCameraStatusesAtom = atomFamily((serverId: string) =>
    atom((get): ReadonlyMap<string, ResourceStatus> => {
        const statuses = new Map<string, ResourceStatus>();
        for (const camera of get(camerasAtom)) {
            if (camera.serverId !== serverId) {
                continue;
            }
            const status = get(cameraStatusAtom(camera.id));
            if (status !== undefined) {
                statuses.set(camera.id, status);
            }
        }
        return statuses;
    }));

/** How many listed cameras the stream last said are offline. */
export const offlineCameraCountAtom = atom((get): number => get(camerasAtom)
    .filter((camera) => get(cameraStatusAtom(camera.id)) === 'offline').length);

interface OnlineCamera {
    id: string;
    serverId: string;
}

const onlineNowAtom = atom((get): OnlineCamera[] => get(camerasAtom)
    .filter((camera) => isOnlineStatus(get(cameraStatusAtom(camera.id))))
    .map(({ id, serverId }) => ({ id, serverId })));

/** The cameras whose latest status says online; the same list until that set changes. */
export const onlineCamerasAtom = selectAtom(
    onlineNowAtom, (cameras): OnlineCamera[] => cameras, dequal);

function recordWrite<T>(writes: PendingWrites<T>, id: string, value: T): PendingWrites<T> {
    return new Map(writes).set(id, { value, recordedAtMs: Date.now(), namedAtMs: null });
}

/** The media_server's answer as a row: the stream, not the answer, says whether it is online. */
function omitStatus(device: FlatDevice): FlatDevice {
    const row = { ...device };
    delete row.status;
    return row;
}

/**
 * Shows a camera its media_server just created until the cloud's list carries it. Nothing
 * reloads after the write itself: its upload reaches the cloud as a `devices` change frame.
 */
export const recordPendingAddAtom = atom(null, (get, set, device: FlatDevice): void => {
    set(pendingAddsAtom, recordWrite(get(pendingAddsAtom), device.id, omitStatus(device)));
});

/**
 * Holds a camera's just-saved talk and audio fields until the cloud's list carries them. An
 * edit holds only the fields that the operator changed, so a later edit lays over the pending
 * one: the first write is still on its way to the cloud.
 */
export const recordPendingTalkEditAtom = atom(null, (get, set, id: string,
    edit: TalkEdit): void => {
    const edits = get(pendingTalkEditsAtom);
    const mergedEdit = { ...edits.get(id)?.value, ...edit };
    set(pendingTalkEditsAtom, recordWrite(edits, id, mergedEdit));
});

/** Shows the name that the cloud took for a camera until the cloud's list carries it. */
export const recordPendingRenameAtom = atom(null, (get, set, id: string, name: string): void => {
    set(pendingRenamesAtom, recordWrite(get(pendingRenamesAtom), id, name));
});

/** Loads the list again at once: a retry after a failed load. */
export const reloadCamerasAtom = snapshot.reloadNow;

function markNamed<T>(writes: PendingWrites<T>, id: string): PendingWrites<T> {
    const write = writes.get(id);
    return write ? new Map(writes).set(id, { ...write, namedAtMs: Date.now() }) : writes;
}

/**
 * What the portal monitor means for the cameras. A load that failed while the cloud was away
 * leaves the rows stale, and when the stream stayed open, no `Reopened` follows to reload them.
 */
export const devicesPortalAtom = atom(null, (_get, set, event: PortalEvent): void => {
    if (event === PortalEvent.Answered) {
        set(snapshot.reload);
    }
});

/** What the notifications stream means for the cameras. */
export const devicesStreamAtom = atom(null, (get, set, input: StreamInput): void => {
    // The gap may have hidden a change frame; every status is replayed by the stream itself.
    if (input.kind === StreamInputKind.Reopened) {
        set(snapshot.reload);
        return;
    }
    if (input.kind === StreamInputKind.Down) {
        set(streamEpochAtom, get(streamEpochAtom) + 1);
        return;
    }
    const { frame } = input;
    // A camera added, removed or changed anywhere in the organization.
    if (frame.type === 'change' && frame.resource === 'devices') {
        set(pendingAddsAtom, markNamed(get(pendingAddsAtom), frame.id));
        set(pendingTalkEditsAtom, markNamed(get(pendingTalkEditsAtom), frame.id));
        set(pendingRenamesAtom, markNamed(get(pendingRenamesAtom), frame.id));
        set(snapshot.reload);
        return;
    }
    if (frame.type !== 'deviceStatusChanged') {
        return;
    }
    const { deviceId, status } = frame;
    // Every reconnect replays every status; one that changes nothing must wake nobody.
    if (get(cameraStatusAtom(deviceId)) === status) {
        return;
    }
    set(statusFrameAtom(deviceId), {
        status,
        epoch: get(streamEpochAtom),
    });
});
